import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { ErrorNotice, Field, Notice, Spinner } from './ui';
import type { Apiary, ApiaryList } from '../types';

/**
 * One card, two callers.
 *
 * A member editing their own record goes through /apiaries/me, which takes no
 * id and can only ever reach their own. The committee goes through
 * /members/{id}/apiaries and /apiaries/{id}, which need members_edit. The
 * difference is one prop, because the screens should look the same — a
 * volunteer helping somebody over the phone is looking at the same thing the
 * member is looking at.
 */
export function ApiariesCard({
  memberId,
  mode,
  canEdit = true,
}: {
  memberId: number;
  mode: 'self' | 'committee';
  canEdit?: boolean;
}) {
  const [data, setData] = useState<ApiaryList | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Apiary | null>(null);

  const listUrl = mode === 'self' ? '/apiaries/me' : `/members/${memberId}/apiaries`;

  function load() {
    api.get<ApiaryList>(listUrl).then(setData).catch(setError);
  }

  useEffect(load, [listUrl]);

  async function remove(apiary: Apiary) {
    if (!window.confirm(`Remove "${apiary.label}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.del(mode === 'self' ? `/apiaries/me/${apiary.id}` : `/apiaries/${apiary.id}`);
      load();
    } catch (failure) {
      setError(failure);
    }
  }

  const total = data?.apiaries.reduce((sum, a) => sum + a.colony_count, 0) ?? 0;
  const declared = data?.declared_colonies ?? null;
  const unplaced = data?.apiaries.filter((a) => !a.is_placed) ?? [];

  return (
    <div className="card">
      <div className="page-head card-head">
        <h2>Apiaries</h2>
        {canEdit && !adding && editing === null ? (
          <button type="button" onClick={() => setAdding(true)}>
            Add an apiary
          </button>
        ) : null}
      </div>

      <ErrorNotice error={error} />

      {data === null && error === null ? <Spinner label="Loading apiaries" /> : null}

      {data !== null && data.apiaries.length === 0 && !adding ? (
        <p className="muted">
          {mode === 'self'
            ? 'None recorded. Adding yours means the association can warn you if an Asian Hornet is found nearby.'
            : 'None recorded for this member.'}
        </p>
      ) : null}

      {data !== null && data.apiaries.length > 0 ? (
        <>
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Apiary</th>
                <th scope="col">Postcode</th>
                <th scope="col">Colonies</th>
                {canEdit ? <th scope="col"></th> : null}
              </tr>
            </thead>
            <tbody>
              {data.apiaries.map((apiary) => (
                <tr key={apiary.id}>
                  <td data-label="Apiary">{apiary.label}</td>
                  <td data-label="Postcode">
                    {apiary.postcode}
                    {!apiary.is_placed ? (
                      <span className="muted block small">not on the map</span>
                    ) : null}
                  </td>
                  <td data-label="Colonies">{apiary.colony_count}</td>
                  {canEdit ? (
                    <td data-label="">
                      <button type="button" className="link" onClick={() => setEditing(apiary)}>
                        Edit
                      </button>
                      <button type="button" className="link" onClick={() => void remove(apiary)}>
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>

          <p className="muted small">
            {total} {total === 1 ? 'colony' : 'colonies'} across{' '}
            {data.apiaries.length === 1 ? 'one apiary' : `${data.apiaries.length} apiaries`}.
          </p>

          {/*
            Both numbers are right. The subscription figure set the BDI band and
            the premium that was paid, and it does not move; this one is what is
            in the field today. Saying so is better than quietly showing one.
          */}
          {declared !== null && declared !== total ? (
            <Notice kind="info">
              This year's subscription was worked out on <strong>{declared}</strong>{' '}
              {declared === 1 ? 'colony' : 'colonies'}, and the BDI premium is based on that figure.
              It is not changed by what is recorded here. If the difference matters for insurance,
              speak to the treasurer.
            </Notice>
          ) : null}

          {unplaced.length > 0 ? (
            <Notice kind="warning">
              {unplaced.length === 1 ? 'One apiary has a postcode' : `${unplaced.length} apiaries have postcodes`}{' '}
              we cannot place on the map, so {unplaced.length === 1 ? 'it' : 'they'} will not show up in a
              search around an Asian Hornet sighting. Check the postcode is a full Isle of Wight one.
            </Notice>
          ) : null}
        </>
      ) : null}

      {adding ? (
        <ApiaryForm
          mode={mode}
          memberId={memberId}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      ) : null}

      {editing !== null ? (
        <ApiaryForm
          mode={mode}
          memberId={memberId}
          apiary={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function ApiaryForm({
  mode,
  memberId,
  apiary,
  onCancel,
  onSaved,
}: {
  mode: 'self' | 'committee';
  memberId: number;
  apiary?: Apiary;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(apiary?.label ?? 'Home');
  const [postcode, setPostcode] = useState(apiary?.postcode ?? '');
  const [colonies, setColonies] = useState(String(apiary?.colony_count ?? 0));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const body = { label, postcode, colony_count: Number(colonies) };

    try {
      if (apiary) {
        await api.put(mode === 'self' ? `/apiaries/me/${apiary.id}` : `/apiaries/${apiary.id}`, body);
      } else {
        await api.post(mode === 'self' ? '/apiaries/me' : `/members/${memberId}/apiaries`, body);
      }
      onSaved();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form wide" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="What it is called"
          required
          value={label}
          hint='Something you would recognise: "Home", "Out apiary at Brading".'
          onChange={(e) => setLabel(e.target.value)}
        />
        <Field
          label="Postcode"
          required
          value={postcode}
          hint="If the apiary has no postcode of its own, use the nearest one."
          onChange={(e) => setPostcode(e.target.value)}
        />
        <Field
          label="Colonies here"
          type="number"
          min={0}
          max={999}
          required
          value={colonies}
          onChange={(e) => setColonies(e.target.value)}
        />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : apiary ? 'Save changes' : 'Add apiary'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
